/**
 * Polygon-related Redux reducers
 * Handles polygon state management for market map
 */
import type { PayloadAction } from "@reduxjs/toolkit";
import type { MapPolygon } from "@survey-types/survey.polygon.types";
import type { currentSurveySliceState } from "@survey-types/survey.core.types";

export const polygonReducers = {
  /**
   * Set all polygons for the current survey
   */
  setPolygons: (
    state: currentSurveySliceState,
    action: PayloadAction<MapPolygon[]>,
  ) => {
    state.polygons = action.payload;
  },

  /**
   * Add a new polygon
   */
  addPolygon: (
    state: currentSurveySliceState,
    action: PayloadAction<MapPolygon>,
  ) => {
    state.polygons.push(action.payload);
  },

  /**
   * Update an existing polygon
   */
  updatePolygonInStore: (
    state: currentSurveySliceState,
    action: PayloadAction<{
      polygonId: string;
      updates: Partial<MapPolygon>;
    }>,
  ) => {
    const { polygonId, updates } = action.payload;
    const index = state.polygons.findIndex((p) => p.polygonId === polygonId);
    if (index !== -1) {
      state.polygons[index] = { ...state.polygons[index], ...updates };
    }
  },

  /**
   * Delete a polygon
   */
  deletePolygonFromStore: (
    state: currentSurveySliceState,
    action: PayloadAction<string>,
  ) => {
    state.polygons = state.polygons.filter(
      (p) => p.polygonId !== action.payload,
    );
  },

  /**
   * Set the currently selected/editing polygon
   */
  setCurrentPolygonId: (
    state: currentSurveySliceState,
    action: PayloadAction<string | null>,
  ) => {
    state.currentPolygonId = action.payload;
  },

  /**
   * Toggle polygon drawing mode
   */
  setPolygonDrawingMode: (
    state: currentSurveySliceState,
    action: PayloadAction<boolean>,
  ) => {
    state.isPolygonDrawingMode = action.payload;
  },

  /**
   * Reorder polygons (for display order)
   */
  reorderPolygons: (
    state: currentSurveySliceState,
    action: PayloadAction<MapPolygon[]>,
  ) => {
    state.polygons = action.payload.map((polygon, index) => ({
      ...polygon,
      order: index,
    }));
  },

  /**
   * Toggle polygon visibility
   */
  togglePolygonVisibility: (
    state: currentSurveySliceState,
    action: PayloadAction<string>,
  ) => {
    const polygon = state.polygons.find((p) => p.polygonId === action.payload);
    if (polygon) {
      polygon.isHidden = !polygon.isHidden;
    }
  },
};
